import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createJSONStorage, persist } from "zustand/middleware";
import { Component, Position, Size } from "../../types";
import { logger } from "@/utils/logger";

// Enhanced responsive types
export interface ResponsiveBreakpoint {
  id: string;
  name: string;
  width: number;
  height: number;
  icon: string;
  mediaQuery: string;
  containerQuery?: string;
  isCustom: boolean;
  order: number;
}

export interface ResponsiveProperty {
  componentId: string;
  property: string;
  values: Record<string, unknown>; // viewport -> value mapping
  isInherited: boolean;
  inheritFrom?: string; // viewport id to inherit from
}

export interface ViewportState {
  id: string;
  isVisible: boolean;
  scale: number;
  position: Position;
  dimensions: Size;
  components: string[]; // component IDs visible in this viewport
  cssRules: CSSRule[];
}

export interface CSSRule {
  id: string;
  selector: string;
  mediaQuery?: string;
  containerQuery?: string;
  properties: Record<string, string>;
  viewport: string;
  componentId?: string;
}

export interface ResponsiveStoreState {
  // Core responsive state
  activeViewport: string;
  availableViewports: ResponsiveBreakpoint[];
  customViewports: ResponsiveBreakpoint[]; // Week 2: User-created custom viewports
  viewportStates: Map<string, ViewportState>;
  responsiveProperties: Map<string, ResponsiveProperty>;

  // Multi-viewport preview
  previewMode: "single" | "split" | "multi";
  visibleViewports: string[];
  syncScrolling: boolean;
  syncSelection: boolean;

  // Viewport transition state
  isTransitioning: boolean;
  transitionProgress: number;
  previousViewport: string | null;
  transitionDuration: number;

  // Week 2: Dual Scale System
  viewportScales: Record<string, number>; // Automatic viewport scaling (0.1-2.0)
  userZoom: number; // Manual user zoom (0.25-4.0)

  // CSS generation
  generatedCSS: string;
  cssRules: Map<string, CSSRule>;
  containerQueries: Map<string, string>;
  mediaQueries: Map<string, string>;

  // Performance optimization
  cullingEnabled: boolean;
  visibilityThreshold: number;
  batchUpdates: boolean;
  updateQueue: ResponsiveUpdate[];

  // Responsive validation
  validationEnabled: boolean;
  validationRules: ValidationRule[];
  validationResults: Map<string, ValidationResult>;

  // Actions
  // Viewport management
  setActiveViewport: (viewportId: string) => void;
  addViewport: (viewport: ResponsiveBreakpoint) => void;
  updateViewport: (id: string, updates: Partial<ResponsiveBreakpoint>) => void;
  updateViewportState: (id: string, updates: Partial<ViewportState>) => void;
  removeViewport: (id: string) => void;
  reorderViewports: (viewportIds: string[]) => void;

  // Week 2: Custom viewport management
  addCustomViewport: (name: string, width: number, height: number) => void;
  removeCustomViewport: (id: string) => void;
  updateCustomViewport: (id: string, updates: Partial<ResponsiveBreakpoint>) => void;
  getCustomViewports: () => ResponsiveBreakpoint[];

  // Viewport transition actions
  beginViewportTransition: (fromId: string, toId: string) => void;
  updateTransitionProgress: (progress: number) => void;
  completeViewportTransition: () => void;
  cancelViewportTransition: () => void;

  // Week 2: Dual Scale System actions
  setViewportScale: (viewportId: string, scale: number) => void;
  getViewportScale: (viewportId: string) => number;
  getEffectiveScale: (viewportId: string) => number;
  setUserZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;

  // Responsive properties
  setResponsiveProperty: (
    componentId: string,
    property: string,
    viewport: string,
    value: unknown,
  ) => void;
  getResponsiveProperty: (
    componentId: string,
    property: string,
    viewport?: string,
  ) => unknown;
  inheritProperty: (
    componentId: string,
    property: string,
    fromViewport: string,
    toViewport: string,
  ) => void;
  clearResponsiveProperty: (
    componentId: string,
    property: string,
    viewport?: string,
  ) => void;

  // Multi-viewport preview
  setPreviewMode: (mode: "single" | "split" | "multi") => void;
  toggleViewportVisibility: (viewportId: string) => void;
  setVisibleViewports: (viewportIds: string[]) => void;
  toggleSyncScrolling: () => void;
  toggleSyncSelection: () => void;

  // CSS generation
  generateCSS: () => void;
  exportCSS: () => string;
  addCSSRule: (rule: CSSRule) => void;
  updateCSSRule: (id: string, updates: Partial<CSSRule>) => void;
  removeCSSRule: (id: string) => void;

  // Performance
  toggleCulling: () => void;
  setCullingThreshold: (threshold: number) => void;
  toggleBatchUpdates: () => void;
  processUpdateQueue: () => void;

  // Validation
  validateResponsiveDesign: () => void;
  addValidationRule: (rule: ValidationRule) => void;
  removeValidationRule: (id: string) => void;
  getValidationResult: (componentId: string) => ValidationResult | undefined;

  // Utility methods
  getViewportComponents: (viewportId: string) => Component[];
  getInheritanceChain: (componentId: string, property: string) => string[];
  getEffectiveValue: (
    componentId: string,
    property: string,
    viewport: string,
  ) => unknown;
  isViewportVisible: (viewportId: string) => boolean;
  getViewportDimensions: (viewportId: string) => Size;

  // Component lifecycle
  onComponentAdded: (component: Component) => void;
  onComponentRemoved: (componentId: string) => void;
  onComponentUpdated: (
    componentId: string,
    updates: Partial<Component>,
  ) => void;
}

interface ResponsiveUpdate {
  type: "property" | "component" | "viewport";
  id: string;
  data: Record<string, unknown>;
  timestamp: number;
}

interface ValidationRule {
  id: string;
  name: string;
  description: string;
  severity: "error" | "warning" | "info";
  check: (component: Component, viewport: string) => ValidationResult;
}

interface ValidationResult {
  passed: boolean;
  message: string;
  severity: "error" | "warning" | "info";
  suggestions?: string[];
}

type ResponsivePersistedState = Pick<
  ResponsiveStoreState,
  "customViewports" | "userZoom" | "activeViewport" | "viewportScales"
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function clampUserZoom(value: number): number {
  return Math.max(0.25, Math.min(4.0, value));
}

function clampViewportScale(value: number): number {
  return Math.max(0.1, Math.min(2.0, value));
}


function parseResponsiveBreakpoint(value: unknown): ResponsiveBreakpoint | null {
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string") return null;
  if (typeof value.name !== "string") return null;
  if (typeof value.width !== "number") return null;
  if (typeof value.height !== "number") return null;
  if (typeof value.icon !== "string") return null;
  if (typeof value.mediaQuery !== "string") return null;
  if (
    value.containerQuery !== undefined &&
    typeof value.containerQuery !== "string"
  ) {
    return null;
  }
  if (typeof value.isCustom !== "boolean") return null;
  if (typeof value.order !== "number") return null;
  if (value.width < 320 || value.width > 7680) return null;
  if (value.height < 240 || value.height > 4320) return null;

  return {
    id: value.id,
    name: value.name,
    width: value.width,
    height: value.height,
    icon: value.icon,
    mediaQuery: value.mediaQuery,
    containerQuery: value.containerQuery,
    isCustom: value.isCustom,
    order: value.order,
  };
}

function sanitizeResponsivePersistedState(
  persistedState: unknown,
): ResponsivePersistedState {
  if (!isRecord(persistedState)) {
    return {
      customViewports: [],
      userZoom: 1.0,
      activeViewport: "desktop",
      viewportScales: {},
    };
  }

  const customViewports = Array.isArray(persistedState.customViewports)
    ? persistedState.customViewports.flatMap((viewport): ResponsiveBreakpoint[] => {
        const parsed = parseResponsiveBreakpoint(viewport);
        return parsed ? [parsed] : [];
      })
    : [];

  const userZoom =
    typeof persistedState.userZoom === "number"
      ? clampUserZoom(persistedState.userZoom)
      : 1.0;

  const activeViewport =
    typeof persistedState.activeViewport === "string"
      ? persistedState.activeViewport
      : "desktop";

  const viewportScales = isRecord(persistedState.viewportScales)
    ? Object.entries(persistedState.viewportScales).reduce<Record<string, number>>(
        (acc, [key, value]) => {
          if (typeof value === "number") {
            acc[key] = clampViewportScale(value);
          }
          return acc;
        },
        {},
      )
    : {};

  return {
    customViewports,
    userZoom,
    activeViewport,
    viewportScales,
  };
}

// Default responsive breakpoints
const DEFAULT_BREAKPOINTS: ResponsiveBreakpoint[] = [
  {
    id: "mobile",
    name: "Mobile",
    width: 375,
    height: 667,
    icon: "📱",
    mediaQuery: "(max-width: 767px)",
    containerQuery: "(max-width: 767px)",
    isCustom: false,
    order: 1,
  },
  {
    id: "tablet",
    name: "Tablet",
    width: 768,
    height: 1024,
    icon: "📱",
    mediaQuery: "(min-width: 768px) and (max-width: 1023px)",
    containerQuery: "(min-width: 768px) and (max-width: 1023px)",
    isCustom: false,
    order: 2,
  },
  {
    id: "desktop",
    name: "Desktop",
    width: 1920,
    height: 1080,
    icon: "🖥️",
    mediaQuery: "(min-width: 1024px)",
    containerQuery: "(min-width: 1024px)",
    isCustom: false,
    order: 3,
  },
];

// Week 2: Comprehensive viewport presets (20+ devices)
export interface ViewportPreset {
  id: string;
  name: string;
  width: number;
  height: number;
  category: 'mobile' | 'tablet' | 'desktop' | 'breakpoint';
  icon: string;
}

export const VIEWPORT_PRESETS: Record<string, ViewportPreset> = {
  // Mobile Devices (7 devices)
  'iphone-se': {
    id: 'iphone-se',
    name: 'iPhone SE',
    width: 375,
    height: 667,
    category: 'mobile',
    icon: '📱',
  },
  'iphone-16-pro': {
    id: 'iphone-16-pro',
    name: 'iPhone 16 Pro',
    width: 402,
    height: 874,
    category: 'mobile',
    icon: '📱',
  },
  'iphone-16-pro-max': {
    id: 'iphone-16-pro-max',
    name: 'iPhone 16 Pro Max',
    width: 440,
    height: 956,
    category: 'mobile',
    icon: '📱',
  },
  'iphone-17-pro-max': {
    id: 'iphone-17-pro-max',
    name: 'iPhone 17 Pro Max',
    width: 440,
    height: 956,
    category: 'mobile',
    icon: '📱',
  },
  'iphone-17-air': {
    id: 'iphone-17-air',
    name: 'iPhone 17 Air',
    width: 420,
    height: 910,
    category: 'mobile',
    icon: '📱',
  },
  'pixel-5': {
    id: 'pixel-5',
    name: 'Google Pixel 5',
    width: 393,
    height: 851,
    category: 'mobile',
    icon: '📱',
  },
  'samsung-s21': {
    id: 'samsung-s21',
    name: 'Samsung Galaxy S21',
    width: 360,
    height: 800,
    category: 'mobile',
    icon: '📱',
  },

  // Tablet Devices (5 devices)
  'ipad-mini': {
    id: 'ipad-mini',
    name: 'iPad Mini',
    width: 768,
    height: 1024,
    category: 'tablet',
    icon: '📱',
  },
  'ipad-air': {
    id: 'ipad-air',
    name: 'iPad Air',
    width: 820,
    height: 1180,
    category: 'tablet',
    icon: '📱',
  },
  'ipad-pro-11': {
    id: 'ipad-pro-11',
    name: 'iPad Pro 11"',
    width: 834,
    height: 1194,
    category: 'tablet',
    icon: '📱',
  },
  'ipad-pro-129': {
    id: 'ipad-pro-129',
    name: 'iPad Pro 12.9"',
    width: 1024,
    height: 1366,
    category: 'tablet',
    icon: '📱',
  },
  'surface-pro': {
    id: 'surface-pro',
    name: 'Surface Pro 7',
    width: 912,
    height: 1368,
    category: 'tablet',
    icon: '📱',
  },

  // Desktop Devices (6 devices)
  'macbook-air': {
    id: 'macbook-air',
    name: 'MacBook Air',
    width: 1280,
    height: 832,
    category: 'desktop',
    icon: '🖥️',
  },
  'macbook-pro-14': {
    id: 'macbook-pro-14',
    name: 'MacBook Pro 14"',
    width: 1512,
    height: 982,
    category: 'desktop',
    icon: '🖥️',
  },
  'macbook-pro-16': {
    id: 'macbook-pro-16',
    name: 'MacBook Pro 16"',
    width: 1728,
    height: 1117,
    category: 'desktop',
    icon: '🖥️',
  },
  'imac-24': {
    id: 'imac-24',
    name: 'iMac 24"',
    width: 1920,
    height: 1080,
    category: 'desktop',
    icon: '🖥️',
  },
  'full-hd': {
    id: 'full-hd',
    name: 'Full HD (1080p)',
    width: 1920,
    height: 1080,
    category: 'desktop',
    icon: '🖥️',
  },
  '4k': {
    id: '4k',
    name: '4K (2160p)',
    width: 3840,
    height: 2160,
    category: 'desktop',
    icon: '🖥️',
  },

  // Common Breakpoints (6 references)
  'xs': {
    id: 'xs',
    name: 'Extra Small',
    width: 375,
    height: 667,
    category: 'breakpoint',
    icon: '📐',
  },
  'sm': {
    id: 'sm',
    name: 'Small',
    width: 640,
    height: 800,
    category: 'breakpoint',
    icon: '📐',
  },
  'md': {
    id: 'md',
    name: 'Medium',
    width: 768,
    height: 1024,
    category: 'breakpoint',
    icon: '📐',
  },
  'lg': {
    id: 'lg',
    name: 'Large',
    width: 1024,
    height: 768,
    category: 'breakpoint',
    icon: '📐',
  },
  'xl': {
    id: 'xl',
    name: 'Extra Large',
    width: 1280,
    height: 800,
    category: 'breakpoint',
    icon: '📐',
  },
  '2xl': {
    id: '2xl',
    name: '2X Large',
    width: 1536,
    height: 864,
    category: 'breakpoint',
    icon: '📐',
  },
};

// Helper function to convert preset to ResponsiveBreakpoint
export const presetToBreakpoint = (preset: ViewportPreset): ResponsiveBreakpoint => ({
  id: preset.id,
  name: preset.name,
  width: preset.width,
  height: preset.height,
  icon: preset.icon,
  mediaQuery: "", // Will be generated based on width
  isCustom: false,
  order: 0, // Will be assigned based on category
});

// Default validation rules
const DEFAULT_VALIDATION_RULES: ValidationRule[] = [
  {
    id: "mobile-touch-target",
    name: "Mobile Touch Target Size",
    description:
      "Interactive elements should be at least 44px in mobile viewports",
    severity: "warning",
    check: (component: Component, viewport: string) => {
      if (
        viewport === "mobile" &&
        (component.type === "button" || component.type === "input")
      ) {
        const minSize = 44;
        const width = typeof component.size.width === "number"
          ? component.size.width
          : Number.parseFloat(component.size.width) || 0;
        const height = typeof component.size.height === "number"
          ? component.size.height
          : Number.parseFloat(component.size.height) || 0;
        const tooSmall =
          width < minSize || height < minSize;
        return {
          passed: !tooSmall,
          message: tooSmall
            ? `Touch target too small: ${width}x${height}px. Minimum: ${minSize}px`
            : "Touch target size is adequate",
          severity: "warning" as const,
          suggestions: tooSmall
            ? [
                "Increase component size to at least 44px",
                "Add padding around the element",
              ]
            : undefined,
        };
      }
      return {
        passed: true,
        message: "Not applicable",
        severity: "info" as const,
      };
    },
  },
  {
    id: "text-readability",
    name: "Text Readability",
    description: "Text should be readable on all viewport sizes",
    severity: "error",
    check: (component: Component, viewport: string) => {
      if (component.type === "text") {
        const fontSize = typeof component.style?.fontSize === 'number'
          ? component.style.fontSize
          : (typeof component.style?.fontSize === 'string'
            ? parseFloat(component.style.fontSize)
            : 16);
        const minFontSize = viewport === "mobile" ? 14 : 12;
        const isReadable = fontSize >= minFontSize;
        return {
          passed: isReadable,
          message: isReadable
            ? "Text size is readable"
            : `Text too small: ${fontSize}px. Minimum for ${viewport}: ${minFontSize}px`,
          severity: isReadable ? ("info" as const) : ("error" as const),
          suggestions: !isReadable
            ? ["Increase font size", "Use relative units (rem, em)"]
            : undefined,
        };
      }
      return {
        passed: true,
        message: "Not applicable",
        severity: "info" as const,
      };
    },
  },
];

export const useResponsiveStore = create<ResponsiveStoreState>()(
  subscribeWithSelector(
    immer(
      persist(
        (set, get) => ({
      // Initial state
      activeViewport: "desktop",
      availableViewports: DEFAULT_BREAKPOINTS,
      customViewports: [], // Week 2: Initialize with no custom viewports
      viewportStates: new Map(
        DEFAULT_BREAKPOINTS.map((bp) => [
          bp.id,
          {
            id: bp.id,
            isVisible: bp.id === "desktop",
            scale: 1,
            position: { x: 0, y: 0, z: 0 },
            dimensions: { width: bp.width, height: bp.height },
            components: [],
            cssRules: [],
          },
        ]),
      ),
      responsiveProperties: new Map(),

      previewMode: "single",
      visibleViewports: ["desktop"],
      syncScrolling: true,
      syncSelection: true,

      isTransitioning: false,
      transitionProgress: 0,
      previousViewport: null,
      transitionDuration: 300,

      // Week 2: Dual Scale System initial state
      viewportScales: {},
      userZoom: 1.0,

      generatedCSS: "",
      cssRules: new Map(),
      containerQueries: new Map(),
      mediaQueries: new Map(),

      cullingEnabled: true,
      visibilityThreshold: 0.1,
      batchUpdates: true,
      updateQueue: [],

      validationEnabled: true,
      validationRules: DEFAULT_VALIDATION_RULES,
      validationResults: new Map(),

      // Viewport management actions
      setActiveViewport: (viewportId: string) => {
        set((state) => {
          // Store previous viewport for transition
          state.previousViewport = state.activeViewport;
          state.activeViewport = viewportId;
          // Ensure active viewport is visible in single mode
          if (state.previewMode === "single") {
            state.visibleViewports = [viewportId];
          }
        });
      },

      // Viewport transition actions
      beginViewportTransition: (fromId: string, toId: string) => {
        set((state) => {
          state.isTransitioning = true;
          state.transitionProgress = 0;
          state.previousViewport = fromId;
        });
      },

      updateTransitionProgress: (progress: number) => {
        set((state) => {
          state.transitionProgress = Math.max(0, Math.min(1, progress));
        });
      },

      completeViewportTransition: () => {
        set((state) => {
          state.isTransitioning = false;
          state.transitionProgress = 1;
          state.previousViewport = null;
        });
      },

      cancelViewportTransition: () => {
        set((state) => {
          state.isTransitioning = false;
          state.transitionProgress = 0;
          // Restore previous viewport if available
          if (state.previousViewport) {
            state.activeViewport = state.previousViewport;
          }
          state.previousViewport = null;
        });
      },

      // Week 2: Dual Scale System action implementations
      setViewportScale: (viewportId: string, scale: number) => {
        set((state) => {
          // Clamp viewport scale between 0.1 (10%) and 2.0 (200%)
          const clampedScale = Math.max(0.1, Math.min(2.0, scale));
          state.viewportScales[viewportId] = clampedScale;

          // Also update the ViewportState scale for consistency
          const viewportState = state.viewportStates.get(viewportId);
          if (viewportState) {
            viewportState.scale = clampedScale;
          }
        });
      },

      getViewportScale: (viewportId: string) => {
        const scale = get().viewportScales[viewportId];
        return scale !== undefined ? scale : 1.0;
      },

      getEffectiveScale: (viewportId: string) => {
        const viewportScale = get().getViewportScale(viewportId);
        const userZoom = get().userZoom;
        return viewportScale * userZoom;
      },

      setUserZoom: (zoom: number) => {
        set((state) => {
          // Clamp user zoom between 0.25 (25%) and 4.0 (400%)
          state.userZoom = Math.max(0.25, Math.min(4.0, zoom));
        });
      },

      zoomIn: () => {
        const currentZoom = get().userZoom;
        const newZoom = currentZoom * 1.25; // Multiply by 1.25
        get().setUserZoom(newZoom);
      },

      zoomOut: () => {
        const currentZoom = get().userZoom;
        const newZoom = currentZoom / 1.25; // Divide by 1.25
        get().setUserZoom(newZoom);
      },

      resetZoom: () => {
        set((state) => {
          state.userZoom = 1.0;
        });
      },

      addViewport: (viewport: ResponsiveBreakpoint) => {
        set((state) => {
          // Prevent duplicate viewports - check if viewport with same ID already exists
          const exists = state.availableViewports.some((vp) => vp.id === viewport.id);
          if (exists) {
            console.log(`[responsive-store] Viewport '${viewport.id}' already exists, skipping add`);
            return;
          }

          state.availableViewports.push(viewport);
          state.viewportStates.set(viewport.id, {
            id: viewport.id,
            isVisible: false,
            scale: 1,
            position: { x: 0, y: 0, z: 0 },
            dimensions: { width: viewport.width, height: viewport.height },
            components: [],
            cssRules: [],
          });
        });
      },

      updateViewport: (id: string, updates: Partial<ResponsiveBreakpoint>) => {
        set((state) => {
          const index = state.availableViewports.findIndex(
            (vp) => vp.id === id,
          );
          if (index !== -1) {
            state.availableViewports[index] = {
              ...state.availableViewports[index],
              ...updates,
            };
          }

          const viewportState = state.viewportStates.get(id);
          if (viewportState && (updates.width || updates.height)) {
            viewportState.dimensions = {
              width: updates.width || viewportState.dimensions.width,
              height: updates.height || viewportState.dimensions.height,
            };
          }
        });
      },

      updateViewportState: (id: string, updates: Partial<ViewportState>) => {
        set((state) => {
          const viewportState = state.viewportStates.get(id);
          if (viewportState) {
            Object.assign(viewportState, updates);
          }
        });
      },

      removeViewport: (id: string) => {
        set((state) => {
          state.availableViewports = state.availableViewports.filter(
            (vp) => vp.id !== id,
          );
          state.viewportStates.delete(id);
          state.visibleViewports = state.visibleViewports.filter(
            (vpId) => vpId !== id,
          );

          // Clear responsive properties for this viewport
          state.responsiveProperties.forEach((prop, key) => {
            delete prop.values[id];
            if (Object.keys(prop.values).length === 0) {
              state.responsiveProperties.delete(key);
            }
          });

          // If active viewport was removed, switch to first available
          if (
            state.activeViewport === id &&
            state.availableViewports.length > 0
          ) {
            state.activeViewport = state.availableViewports[0].id;
          }
        });
      },

      reorderViewports: (viewportIds: string[]) => {
        set((state) => {
          const reorderedViewports: ResponsiveBreakpoint[] = [];
          viewportIds.forEach((id, index) => {
            const viewport = state.availableViewports.find(
              (vp) => vp.id === id,
            );
            if (viewport) {
              reorderedViewports.push({ ...viewport, order: index + 1 });
            }
          });
          state.availableViewports = reorderedViewports;
        });
      },

      // Week 2: Custom viewport management actions
      addCustomViewport: (name: string, width: number, height: number) => {
        set((state) => {
          // Validate dimensions (320-7680px width, 240-4320px height per Week 2 spec)
          const validatedWidth = Math.max(320, Math.min(7680, width));
          const validatedHeight = Math.max(240, Math.min(4320, height));

          // Generate unique ID based on dimensions
          const id = `custom-${validatedWidth}x${validatedHeight}-${Date.now()}`;

          // Determine category based on width
          let category: 'mobile' | 'tablet' | 'desktop' | 'breakpoint' = 'desktop';
          if (validatedWidth < 768) {
            category = 'mobile';
          } else if (validatedWidth < 1024) {
            category = 'tablet';
          }

          // Create media query based on width
          const mediaQuery = `(max-width: ${validatedWidth}px)`;

          const customViewport: ResponsiveBreakpoint = {
            id,
            name,
            width: validatedWidth,
            height: validatedHeight,
            icon: '📐', // Custom icon for user-created viewports
            mediaQuery,
            containerQuery: `(max-width: ${validatedWidth}px)`,
            isCustom: true,
            order: state.availableViewports.length + 1,
          };

          // Add to both customViewports and availableViewports
          state.customViewports.push(customViewport);
          state.availableViewports.push(customViewport);

          // Create viewport state
          state.viewportStates.set(id, {
            id,
            isVisible: false,
            scale: 1,
            position: { x: 0, y: 0, z: 0 },
            dimensions: { width: validatedWidth, height: validatedHeight },
            components: [],
            cssRules: [],
          });
        });
      },

      removeCustomViewport: (id: string) => {
        set((state) => {
          // Only allow removing custom viewports (check isCustom flag)
          const viewport = state.availableViewports.find((vp) => vp.id === id);
          if (!viewport || !viewport.isCustom) {
            console.warn(`Cannot remove non-custom viewport: ${id}`);
            return;
          }

          // Remove from customViewports
          state.customViewports = state.customViewports.filter(
            (vp) => vp.id !== id,
          );

          // Remove from availableViewports
          state.availableViewports = state.availableViewports.filter(
            (vp) => vp.id !== id,
          );

          // Remove viewport state
          state.viewportStates.delete(id);

          // Remove from visible viewports
          state.visibleViewports = state.visibleViewports.filter(
            (vpId) => vpId !== id,
          );

          // Clear responsive properties for this viewport
          state.responsiveProperties.forEach((prop, key) => {
            delete prop.values[id];
            if (Object.keys(prop.values).length === 0) {
              state.responsiveProperties.delete(key);
            }
          });

          // If active viewport was removed, switch to first available
          if (
            state.activeViewport === id &&
            state.availableViewports.length > 0
          ) {
            state.activeViewport = state.availableViewports[0].id;
          }
        });
      },

      updateCustomViewport: (
        id: string,
        updates: Partial<ResponsiveBreakpoint>,
      ) => {
        set((state) => {
          // Only allow updating custom viewports
          const customIndex = state.customViewports.findIndex(
            (vp) => vp.id === id,
          );
          if (customIndex === -1) {
            console.warn(`Cannot update non-custom viewport: ${id}`);
            return;
          }

          const availableIndex = state.availableViewports.findIndex(
            (vp) => vp.id === id,
          );

          if (customIndex !== -1) {
            // Validate width/height updates
            if (updates.width !== undefined) {
              updates.width = Math.max(320, Math.min(7680, updates.width));
            }
            if (updates.height !== undefined) {
              updates.height = Math.max(240, Math.min(4320, updates.height));
            }

            // Update in customViewports
            state.customViewports[customIndex] = {
              ...state.customViewports[customIndex],
              ...updates,
            };

            // Update in availableViewports
            if (availableIndex !== -1) {
              state.availableViewports[availableIndex] = {
                ...state.availableViewports[availableIndex],
                ...updates,
              };
            }

            // Update viewport state dimensions if width/height changed
            const viewportState = state.viewportStates.get(id);
            if (viewportState && (updates.width || updates.height)) {
              viewportState.dimensions = {
                width: updates.width || viewportState.dimensions.width,
                height: updates.height || viewportState.dimensions.height,
              };
            }
          }
        });
      },

      getCustomViewports: () => {
        return get().customViewports;
      },

      // Responsive property actions
      setResponsiveProperty: (
        componentId: string,
        property: string,
        viewport: string,
        value: unknown,
      ) => {
        set((state) => {
          const key = `${componentId}:${property}`;
          let responsiveProperty = state.responsiveProperties.get(key);

          if (!responsiveProperty) {
            responsiveProperty = {
              componentId,
              property,
              values: {},
              isInherited: false,
            };
            state.responsiveProperties.set(key, responsiveProperty);
          }

          responsiveProperty.values[viewport] = value;
          responsiveProperty.isInherited = false;

          // Add to update queue if batching is enabled
          if (state.batchUpdates) {
            state.updateQueue.push({
              type: "property",
              id: key,
              data: { viewport, value },
              timestamp: Date.now(),
            });
          }
        });

        // Trigger CSS regeneration
        get().generateCSS();
      },

      getResponsiveProperty: (
        componentId: string,
        property: string,
        viewport?: string,
      ) => {
        const key = `${componentId}:${property}`;
        const responsiveProperty = get().responsiveProperties.get(key);

        if (!responsiveProperty) return undefined;

        const targetViewport = viewport || get().activeViewport;

        // Return direct value if exists
        if (responsiveProperty.values[targetViewport] !== undefined) {
          return responsiveProperty.values[targetViewport];
        }

        // Implement cascading inheritance - mobile-first approach
        // Check from desktop -> tablet -> mobile for the first available value
        const state = get();

        // If we're looking for mobile and it doesn't exist, check tablet, then desktop
        // If we're looking for tablet and it doesn't exist, check desktop
        let searchOrder: string[] = [];

        switch (targetViewport) {
          case "mobile":
            searchOrder = ["tablet", "desktop"];
            break;
          case "tablet":
            searchOrder = ["desktop"];
            break;
          case "desktop":
            searchOrder = []; // Desktop doesn't inherit
            break;
          default:
            // For custom viewports, check from largest to smallest
            searchOrder = state.availableViewports
              .sort((a, b) => b.width - a.width)
              .map((vp) => vp.id)
              .filter((id) => id !== targetViewport);
            break;
        }

        // Look for inherited value and calculate proportional equivalent
        for (const vpId of searchOrder) {
          if (responsiveProperty.values[vpId] !== undefined) {
            const inheritedValue = responsiveProperty.values[vpId];

            // For numeric position/size properties, calculate proportional value
            if (typeof inheritedValue === 'number' &&
                (property.includes('position.') || property.includes('size.'))) {
              const sourceViewport = state.availableViewports.find(vp => vp.id === vpId);
              const targetViewportObj = state.availableViewports.find(vp => vp.id === targetViewport);

              if (sourceViewport && targetViewportObj) {
                // Import calculateProportionalValue for runtime calculation
                const propertyType = property.includes('position.') ? 'position' : 'size';
                const widthRatio = targetViewportObj.width / sourceViewport.width;
                const heightRatio = targetViewportObj.height / sourceViewport.height;

                // Calculate proportional value based on property type
                const scaleFactor = propertyType === 'size'
                  ? Math.min(widthRatio, heightRatio)
                  : (widthRatio + heightRatio) / 2;

                return inheritedValue * scaleFactor;
              }
            }

            // Return inherited value as-is for non-numeric properties
            return inheritedValue;
          }
        }

        return undefined;
      },

      inheritProperty: (
        componentId: string,
        property: string,
        fromViewport: string,
        toViewport: string,
      ) => {
        const fromValue = get().getResponsiveProperty(
          componentId,
          property,
          fromViewport,
        );
        if (fromValue !== undefined) {
          set((state) => {
            const key = `${componentId}:${property}`;
            let responsiveProperty = state.responsiveProperties.get(key);

            if (!responsiveProperty) {
              responsiveProperty = {
                componentId,
                property,
                values: {},
                isInherited: false,
              };
              state.responsiveProperties.set(key, responsiveProperty);
            }

            responsiveProperty.values[toViewport] = fromValue;
            responsiveProperty.isInherited = true;
            responsiveProperty.inheritFrom = fromViewport;
          });

          get().generateCSS();
        }
      },

      clearResponsiveProperty: (
        componentId: string,
        property: string,
        viewport?: string,
      ) => {
        set((state) => {
          const key = `${componentId}:${property}`;
          const responsiveProperty = state.responsiveProperties.get(key);

          if (responsiveProperty) {
            if (viewport) {
              delete responsiveProperty.values[viewport];
              if (Object.keys(responsiveProperty.values).length === 0) {
                state.responsiveProperties.delete(key);
              }
            } else {
              state.responsiveProperties.delete(key);
            }
          }
        });

        get().generateCSS();
      },

      // Multi-viewport preview actions
      setPreviewMode: (mode: "single" | "split" | "multi") => {
        set((state) => {
          state.previewMode = mode;

          switch (mode) {
            case "single":
              state.visibleViewports = [state.activeViewport];
              break;
            case "split":
              state.visibleViewports = state.availableViewports
                .slice(0, 2)
                .map((vp) => vp.id);
              break;
            case "multi":
              state.visibleViewports = state.availableViewports.map(
                (vp) => vp.id,
              );
              break;
          }

          // Update viewport visibility states
          state.viewportStates.forEach((viewportState, id) => {
            viewportState.isVisible = state.visibleViewports.includes(id);
          });
        });
      },

      toggleViewportVisibility: (viewportId: string) => {
        set((state) => {
          const isVisible = state.visibleViewports.includes(viewportId);

          if (isVisible) {
            state.visibleViewports = state.visibleViewports.filter(
              (id) => id !== viewportId,
            );
          } else {
            state.visibleViewports.push(viewportId);
          }

          const viewportState = state.viewportStates.get(viewportId);
          if (viewportState) {
            viewportState.isVisible = !isVisible;
          }
        });
      },

      setVisibleViewports: (viewportIds: string[]) => {
        set((state) => {
          state.visibleViewports = viewportIds;

          // Update viewport visibility states
          state.viewportStates.forEach((viewportState, id) => {
            viewportState.isVisible = viewportIds.includes(id);
          });
        });
      },

      toggleSyncScrolling: () => {
        set((state) => {
          state.syncScrolling = !state.syncScrolling;
        });
      },

      toggleSyncSelection: () => {
        set((state) => {
          state.syncSelection = !state.syncSelection;
        });
      },

      // CSS generation actions
      generateCSS: () => {
        const state = get();
        const cssRules: string[] = [];
        const mediaQueries = new Map<string, string[]>();

        // Helper function to format property value
        const formatValue = (property: string, value: unknown): string => {
          if (value === null || value === undefined) return "";

          if (typeof value === "object") {
            const recordValue = value as Record<string, unknown>;
            if (property.includes("position")) {
              if (typeof recordValue.x === "number") return `${recordValue.x}px`;
              if (typeof recordValue.y === "number") return `${recordValue.y}px`;
            }
            if (property.includes("size")) {
              if (typeof recordValue.width === "number") return `${recordValue.width}px`;
              if (typeof recordValue.height === "number") return `${recordValue.height}px`;
            }
            return JSON.stringify(value);
          }

          if (typeof value === "number") {
            const pxProperties = [
              "width",
              "height",
              "top",
              "left",
              "right",
              "bottom",
              "margin",
              "padding",
              "border-radius",
              "font-size",
            ];
            if (pxProperties.some((prop) => property.includes(prop))) {
              return `${value}px`;
            }
            return String(value);
          }

          return String(value);
        };

        // Helper to convert camelCase to kebab-case
        const camelToKebab = (str: string): string => {
          return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
        };

        // Generate CSS for each responsive property
        state.responsiveProperties.forEach((prop) => {
          Object.entries(prop.values).forEach(([viewport, value]) => {
            const breakpoint = state.availableViewports.find(
              (bp) => bp.id === viewport,
            );
            if (!breakpoint || value === undefined) return;

            const selector = `[data-component-id="${prop.componentId}"]`;
            let cssProperty = prop.property;

            // Handle nested properties (like position.x, size.width)
            if (cssProperty.includes(".")) {
              const parts = cssProperty.split(".");
              if (parts[0] === "position") {
                if (parts[1] === "x") cssProperty = "left";
                if (parts[1] === "y") cssProperty = "top";
                if (parts[1] === "z") cssProperty = "z-index";
              } else if (parts[0] === "size") {
                if (parts[1] === "width") cssProperty = "width";
                if (parts[1] === "height") cssProperty = "height";
              } else if (parts[0] === "style") {
                cssProperty = camelToKebab(parts[1]);
              }
            } else {
              cssProperty = camelToKebab(cssProperty);
            }

            const cssValue = formatValue(cssProperty, value);
            if (!cssValue) return;

            const rule = `${cssProperty}: ${cssValue};`;

            if (breakpoint.mediaQuery && viewport !== "desktop") {
              if (!mediaQueries.has(breakpoint.mediaQuery)) {
                mediaQueries.set(breakpoint.mediaQuery, []);
              }
              mediaQueries
                .get(breakpoint.mediaQuery)!
                .push(`  ${selector} {\n    ${rule}\n  }`);
            } else {
              // Base styles (desktop/no media query)
              cssRules.push(`${selector} {\n  ${rule}\n}`);
            }
          });
        });

        // Combine base rules and media queries
        let generatedCSS = cssRules.join("\n\n");

        if (mediaQueries.size > 0) {
          generatedCSS += "\n\n";
          mediaQueries.forEach((rules, query) => {
            generatedCSS += `@media ${query} {\n${rules.join("\n\n")}\n}\n\n`;
          });
        }

        set((state) => {
          state.generatedCSS = generatedCSS.trim();
        });
      },

      exportCSS: () => {
        get().generateCSS();
        return get().generatedCSS;
      },

      addCSSRule: (rule: CSSRule) => {
        set((state) => {
          state.cssRules.set(rule.id, rule);
        });
      },

      updateCSSRule: (id: string, updates: Partial<CSSRule>) => {
        set((state) => {
          const rule = state.cssRules.get(id);
          if (rule) {
            state.cssRules.set(id, { ...rule, ...updates });
          }
        });
      },

      removeCSSRule: (id: string) => {
        set((state) => {
          state.cssRules.delete(id);
        });
      },

      // Performance actions
      toggleCulling: () => {
        set((state) => {
          state.cullingEnabled = !state.cullingEnabled;
        });
      },

      setCullingThreshold: (threshold: number) => {
        set((state) => {
          state.visibilityThreshold = threshold;
        });
      },

      toggleBatchUpdates: () => {
        set((state) => {
          state.batchUpdates = !state.batchUpdates;
        });
      },

      processUpdateQueue: () => {
        const state = get();
        const updates = [...state.updateQueue];

        set((state) => {
          state.updateQueue = [];
        });

        // Process batched updates
        updates.forEach((update) => {
          // Implementation would handle batched processing
          console.log("Processing batched update:", update);
        });
      },

      // Validation actions
      validateResponsiveDesign: () => {
        const results = new Map<string, ValidationResult>();

        // This would be implemented with actual component data
        // For now, we'll create a placeholder implementation

        set((state) => {
          state.validationResults = results;
        });
      },

      addValidationRule: (rule: ValidationRule) => {
        set((state) => {
          state.validationRules.push(rule);
        });
      },

      removeValidationRule: (id: string) => {
        set((state) => {
          state.validationRules = state.validationRules.filter(
            (rule) => rule.id !== id,
          );
        });
      },

      getValidationResult: (componentId: string) => {
        return get().validationResults.get(componentId);
      },

      // Utility methods
      getViewportComponents: (_viewportId: string) => {
        // This would be implemented to return components for a specific viewport
        return [];
      },

      getInheritanceChain: (componentId: string, property: string) => {
        const state = get();
        const key = `${componentId}:${property}`;
        const responsiveProperty = state.responsiveProperties.get(key);

        if (!responsiveProperty) return [];

        // Return viewports that have values for this property, in cascade order
        const chain: string[] = [];
        const inheritanceOrder = ["desktop", "tablet", "mobile"];

        for (const vpId of inheritanceOrder) {
          if (responsiveProperty.values[vpId] !== undefined) {
            chain.push(vpId);
          }
        }

        // Add custom viewports that have values
        const customViewports = state.availableViewports
          .filter((vp) => !inheritanceOrder.includes(vp.id))
          .sort((a, b) => b.width - a.width); // Largest to smallest

        for (const vp of customViewports) {
          if (responsiveProperty.values[vp.id] !== undefined) {
            chain.push(vp.id);
          }
        }

        return chain;
      },

      getEffectiveValue: (
        componentId: string,
        property: string,
        viewport: string,
      ) => {
        return get().getResponsiveProperty(componentId, property, viewport);
      },

      isViewportVisible: (viewportId: string) => {
        return get().visibleViewports.includes(viewportId);
      },

      getViewportDimensions: (viewportId: string) => {
        const viewportState = get().viewportStates.get(viewportId);
        return viewportState
          ? viewportState.dimensions
          : { width: 1920, height: 1080 };
      },

      // Component lifecycle
      onComponentAdded: (component: Component) => {
        const state = get();

        // Add component to all visible viewports
        state.visibleViewports.forEach((viewportId) => {
          const viewportState = state.viewportStates.get(viewportId);
          if (viewportState) {
            set((_state) => {
              viewportState.components.push(component.id);
            });
          }
        });
      },

      onComponentRemoved: (componentId: string) => {
        set((state) => {
          // Remove from all viewport states
          state.viewportStates.forEach((viewportState) => {
            viewportState.components = viewportState.components.filter(
              (id) => id !== componentId,
            );
          });

          // Clear all responsive properties for this component
          const keysToRemove: string[] = [];
          state.responsiveProperties.forEach((prop, key) => {
            if (prop.componentId === componentId) {
              keysToRemove.push(key);
            }
          });
          keysToRemove.forEach((key) => state.responsiveProperties.delete(key));

          // Clear validation results
          state.validationResults.delete(componentId);
        });
      },

      onComponentUpdated: (
        componentId: string,
        updates: Partial<Component>,
      ) => {
        // Trigger validation if component properties changed
        if (updates.size || updates.position || updates.style) {
          get().validateResponsiveDesign();
        }
      },
        }),
        {
          name: 'terramatrix-viewport-preferences',
          version: 1,
          storage:
            typeof window !== "undefined"
              ? createJSONStorage<ResponsivePersistedState>(
                  () => window.localStorage,
                )
              : undefined,
          partialize: (state): ResponsivePersistedState => ({
            customViewports: state.customViewports,
            userZoom: state.userZoom,
            activeViewport: state.activeViewport,
            viewportScales: state.viewportScales,
          }),
          migrate: (persistedState: unknown, version: number): ResponsivePersistedState => {
            // Migration logic for future version upgrades
            if (version === 0) {
              return sanitizeResponsivePersistedState(persistedState);
            }

            return sanitizeResponsivePersistedState(persistedState);
          },
          onRehydrateStorage: () => {
            return (state, error) => {
              if (error) {
                console.warn('Failed to rehydrate viewport preferences from localStorage:', error);
                // Fallback to defaults on error
                return;
              }

              if (state) {
                logger.debug("Rehydrated viewport preferences", {
                  customViewportsCount: state.customViewports?.length ?? 0,
                  userZoom: state.userZoom,
                  activeViewport: state.activeViewport,
                  viewportScalesCount: Object.keys(state.viewportScales ?? {}).length,
                });

                // Sync visibleViewports with restored activeViewport
                if (state.activeViewport && !state.visibleViewports.includes(state.activeViewport)) {
                  state.visibleViewports = [state.activeViewport];
                }
              }
            };
          },
        },
      ),
    ),
  ),
);

// Selectors for common use cases
export const useActiveViewport = () => {
  const { activeViewport, availableViewports } = useResponsiveStore();
  return {
    activeViewport,
    viewportConfig: availableViewports.find((vp) => vp.id === activeViewport),
  };
};

export const useViewportStates = () => {
  const { viewportStates, visibleViewports } = useResponsiveStore();
  return { viewportStates, visibleViewports };
};

export const useResponsiveProperties = (componentId: string) => {
  const store = useResponsiveStore();

  return {
    getProperty: (property: string, viewport?: string) =>
      store.getResponsiveProperty(componentId, property, viewport),
    setProperty: (property: string, viewport: string, value: unknown) =>
      store.setResponsiveProperty(componentId, property, viewport, value),
    clearProperty: (property: string, viewport?: string) =>
      store.clearResponsiveProperty(componentId, property, viewport),
  };
};

export const useResponsiveValidation = (componentId: string) => {
  const { getValidationResult, validateResponsiveDesign } =
    useResponsiveStore();

  return {
    validationResult: getValidationResult(componentId),
    validate: validateResponsiveDesign,
  };
};
